import { FC } from 'react';
import { Check } from 'lucide-react';
import { contrastColor } from 'features/common/components/Punchup/primitives/colorUtils';
import styles from './PunchupColorPicker.module.css';

export interface PunchupColorPickerProps {
	value: string;
	onChange: (color: string) => void;
}

/**
 * 20 preset tag colors in a 2×10 grid.
 * Row 1 — vivid/mid: red, lime, green, teal, cyan, blue, violet, pink, grey, dark grey.
 * Row 2 — deep/dark variants of the same hues.
 * Orange and amber hues are excluded — too close to the site primary (#FE5E25).
 */
export const PRESET_COLORS = [
	// Row 1 — vivid
	'#EF4444',
	'#84CC16',
	'#22C55E',
	'#14B8A6',
	'#06B6D4',
	'#3B82F6',
	'#8B5CF6',
	'#EC4899',
	'#6B7280',
	'#374151',
	// Row 2 — deep / dark variants
	'#B91C1C',
	'#4D7C0F',
	'#166534',
	'#0F766E',
	'#155E75',
	'#1E40AF',
	'#5B21B6',
	'#9D174D',
	'#9CA3AF',
	'#111827',
] as const;

/**
 * PunchupColorPicker
 *
 * 2×10 grid of 20 preset tag colors.
 * Hover: scale up + full opacity.
 * Selected: WCAG-contrast checkmark only — no border or ring.
 */
export const PunchupColorPicker: FC<PunchupColorPickerProps> = ({ value, onChange }) => (
	<div className={styles.grid}>
		{PRESET_COLORS.map((color) => {
			const selected = value === color;
			return (
				<button
					key={color}
					type="button"
					className={[styles.cell, selected ? styles.cellSelected : ''].filter(Boolean).join(' ')}
					style={{ backgroundColor: color }}
					onClick={() => onChange(color)}
					aria-label={color}
					aria-pressed={selected}
				>
					{selected && <Check size={12} strokeWidth={3} color={contrastColor(color)} aria-hidden />}
				</button>
			);
		})}
	</div>
);
